/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useMemo } from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createHook } from '@atlassian/react-sweet-state';
import { useQueryParam } from '@atlassian/react-resource-router';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api/use-preview-panel-actions';
import { useCurrentPreviewPanel } from '@atlassian/preview-panels-api/use-current-preview-panel';
import { fg } from '@atlassian/jira-feature-gating';
import type { RightSidebar, RightSidebarState } from '../../../../common/types.tsx';
import { selectCurrentRightSidebar } from '../../actions/right-sidebar/index.tsx';
import { layoutStore, useLayoutStoreActions } from '../../store/index.tsx';

const useActiveRightSidebar = createHook(layoutStore, {
	selector: (state) => {
		const rightSidebarState = selectCurrentRightSidebar(state.content.rightSidebar);

		if (!rightSidebarState) return null;

		return {
			...rightSidebarState,
			shouldRender: !state.isChromeless,
		};
	},
});

type ExtendedRightSidebarState = {
	shouldRender: boolean;
} & RightSidebar;

export const useActiveRightSidebarState = (): ExtendedRightSidebarState | null => {
	const [rightSidebar] = useActiveRightSidebar();
	return rightSidebar;
};

export type UseRightSidebarStateType = typeof useActiveRightSidebarState;

const useRightSidebar = createHook(layoutStore, {
	selector: (state) => state.content.rightSidebar,
});

export const useRightSidebarState = (): RightSidebarState => {
	const [rightSidebarState] = useRightSidebar();
	return rightSidebarState;
};

export const useRightSidebarController = (): {
	setRightSidebarState: (newState: RightSidebarState) => void | Promise<void>;
	closeRightSidebar: (panelId: string) => void | Promise<void>;
	openRightSidebar: (
		panelId: string,
		width: number,
		isMinimised?: boolean | undefined,
		toTop?: boolean | undefined,
	) => void | Promise<void>;
	closeCurrentRightSidebar: () => void | Promise<void>;
	closeAllRightSidebar: () => void | Promise<void>;
	minimiseRightSidebar: (panelId: string) => void | Promise<void>;
} => {
	const [
		,
		{
			setRightSidebarState,
			openRightSidebar: openRightSidebarAction,
			closeRightSidebar,
			closeCurrentRightSidebar,
			closeAllRightSidebar,
			minimiseRightSidebar,
		},
	] = useLayoutStoreActions();

	let closePreviewPanel: (() => void) | undefined;
	let isIssuePreviewPanelOpen = false;
	let setSelectedIssue: ((value: string | undefined) => void) | undefined;

	// eslint-disable-next-line jira/ff/no-preconditioning
	if (fg('jira-glance-panel') && fg('jira_preview_panel_m_1_rollout')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const [, setSelectedIssueHook] = useQueryParam('selectedIssue');
		setSelectedIssue = setSelectedIssueHook;

		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { close } = usePreviewPanelsActions();
		closePreviewPanel = close;

		// eslint-disable-next-line react-hooks/rules-of-hooks
		const panel = useCurrentPreviewPanel();
		isIssuePreviewPanelOpen = panel?.type === 'jira-issue' && panel?.id?.startsWith('jira-issue/');
	}

	const openRightSidebar =
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('jira-glance-panel') && fg('jira_preview_panel_m_1_rollout')
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useCallback(
					(panelId: string, width: number, isMinimised = false, toTop = false) => {
						if (panelId !== 'jsw-nux-onboarding-checklist') {
							openRightSidebarAction(panelId, width, isMinimised, toTop);
							if (isIssuePreviewPanelOpen) {
								setSelectedIssue?.(undefined);
							}
							closePreviewPanel?.();
						} else if (!isIssuePreviewPanelOpen) {
							openRightSidebarAction(panelId, width, isMinimised, toTop);
						}
					},
					[closePreviewPanel, isIssuePreviewPanelOpen, openRightSidebarAction, setSelectedIssue],
				)
			: openRightSidebarAction;

	return useMemo(
		() => ({
			setRightSidebarState,
			closeRightSidebar,
			openRightSidebar,
			closeCurrentRightSidebar,
			closeAllRightSidebar,
			minimiseRightSidebar,
		}),
		[
			closeAllRightSidebar,
			closeCurrentRightSidebar,
			closeRightSidebar,
			openRightSidebar,
			setRightSidebarState,
			minimiseRightSidebar,
		],
	);
};

export type UseRightSidebarControllerType = typeof useRightSidebarController;
